'use client'

import { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { GripVertical, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLanguage } from '@/context/LanguageContext'

// Same shape/fields as `ProductSelect`'s catalog query (name + thumbnail per
// language) — duplicated rather than shared because this component's
// selection semantics differ (ordered, append-only add + a separate drag
// list for reorder/remove, vs. `ProductSelect`'s toggle-select chips), and
// `ProductSelect` itself is shared with `CollectionForm` where that toggle
// behavior is still wanted.
const GET_PRODUCTS_FOR_GRID_PICKER = gql`
  query GET_PRODUCTS_FOR_GRID_PICKER($language: Language!) {
    findManyProductContainer {
      id
      single_product {
        product_profiles(where: { language: { equals: $language } }) {
          language
          name
        }
        images {
          src_thumbnail
        }
      }
      advanced_product {
        advanced_product_profiles(where: { language: { equals: $language } }) {
          name
          language
        }
        images {
          src_thumbnail
        }
      }
    }
  }
`

interface GridProductPickerProps {
  field: {
    value: Array<{ id: number }> | undefined
    onChange: (value: Array<{ id: number }>) => void
  }
}

interface ProductOption {
  id: number
  name: string
  image?: string
}

/**
 * Ordered, reorderable product picker for a `product_grid` block's manual
 * picks. Unlike `ProductSelect` (a toggle multi-select used elsewhere for
 * unordered relations), this keeps `field.value` as an ORDERED `{ id }[]`
 * list: newly added products always append at the end, and the "Selected
 * Products" list below is drag-reorderable (mirrors the "Selected" pattern
 * used by `FilterSettings` and this same form's category/page pickers, via
 * the same `react-beautiful-dnd` the repo already uses elsewhere — there is
 * no dnd-kit usage in this codebase).
 */
export default function GridProductPicker({ field }: GridProductPickerProps) {
  const { language } = useLanguage()
  const [search, setSearch] = useState('')
  const { data, loading, error } = useQuery(GET_PRODUCTS_FOR_GRID_PICKER, {
    variables: { language },
  })

  const productsById = useMemo(() => {
    const map = new Map<number, ProductOption>()
    for (const p of data?.findManyProductContainer ?? []) {
      const profile = p.single_product
        ? p.single_product.product_profiles?.find(
            (pr: { language: string }) => pr.language === language
          ) || p.single_product.product_profiles?.[0]
        : p.advanced_product?.advanced_product_profiles?.find(
            (pr: { language: string }) => pr.language === language
          ) || p.advanced_product?.advanced_product_profiles?.[0]
      const image =
        p.single_product?.images?.[0]?.src_thumbnail ??
        p.advanced_product?.images?.[0]?.src_thumbnail ??
        undefined
      map.set(p.id, {
        id: p.id,
        name: profile?.name || 'Unnamed Product',
        image,
      })
    }
    return map
  }, [data, language])

  const selectedIds = (field.value ?? []).map(v => v.id)
  const selectedIdSet = new Set(selectedIds)

  const searchLower = search.trim().toLowerCase()
  const availableProducts = Array.from(productsById.values())
    .filter(p => !selectedIdSet.has(p.id))
    .filter(p => !searchLower || p.name.toLowerCase().includes(searchLower))
    .slice(0, 30)

  const handleAdd = (id: number) => {
    if (selectedIdSet.has(id)) return
    field.onChange([...(field.value ?? []), { id }])
    setSearch('')
  }

  const handleRemove = (id: number) => {
    field.onChange((field.value ?? []).filter(v => v.id !== id))
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const current = [...(field.value ?? [])]
    const [moved] = current.splice(result.source.index, 1)
    current.splice(result.destination.index, 0, moved)
    field.onChange(current)
  }

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">Selected Products</Label>
        {selectedIds.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-2">
            No products selected
          </p>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="selected-grid-products">
              {provided => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="mt-2 space-y-2"
                >
                  {selectedIds.map((id, index) => {
                    const product = productsById.get(id)
                    return (
                      <Draggable
                        key={id}
                        draggableId={String(id)}
                        index={index}
                      >
                        {dragProvided => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className="flex items-center gap-2 rounded-md border bg-white px-3 py-2"
                          >
                            <div
                              {...dragProvided.dragHandleProps}
                              className="cursor-grab text-muted-foreground shrink-0"
                            >
                              <GripVertical className="h-4 w-4" />
                            </div>
                            {product?.image && (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-8 h-8 object-cover rounded shrink-0"
                              />
                            )}
                            <span className="flex-1 min-w-0 truncate text-sm">
                              {product ? product.name : `Product #${id}`}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0 text-red-500 hover:text-red-700"
                              onClick={() => handleRemove(id)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <div>
        <Label className="text-sm font-medium">Add Products</Label>
        <Input
          placeholder="Search products..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="mt-2"
        />
        {loading && (
          <p className="text-sm text-muted-foreground mt-2">
            Loading products...
          </p>
        )}
        {error && (
          <p className="text-sm text-destructive mt-2">
            Failed to load products
          </p>
        )}
        {!loading && !error && (
          <div className="mt-2 max-h-56 overflow-y-auto space-y-1">
            {availableProducts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No matching products
              </p>
            ) : (
              availableProducts.map(product => (
                <button
                  type="button"
                  key={product.id}
                  onClick={() => handleAdd(product.id)}
                  className="w-full flex items-center gap-2 rounded-md border px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                >
                  {product.image && (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-8 h-8 object-cover rounded shrink-0"
                    />
                  )}
                  <span className="flex-1 min-w-0 truncate text-sm">
                    {product.name}
                  </span>
                  <Plus className="h-4 w-4 text-green-500 shrink-0" />
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
